import React, { Suspense, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Dashboard,
  AdminPanelSettings,
  FolderSpecial,
  Settings as SettingsIcon,
  AutoAwesome,
  Draw,
  Storefront,
  Group,
  Insights,
  ExpandMore,
} from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useDeferredReady from '../../hooks/useDeferredReady';
import { getRoleAppearance } from '../../utils/roleAppearance';
import { canAccessModule, canInviteTalent, canUseAnnotator, canUseDatasetWorkspace, canUseVendorTools, isAdmin, isBuyerOperator } from '../../utils/accessControl';

const NotificationCenter = React.lazy(() => import('../NotificationCenter'));
const SmartSearchBar = React.lazy(() => import('../SmartSearchBar'));

const Navbar = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [solutionsAnchor, setSolutionsAnchor] = useState(null);
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleMoreMenu = (event) => {
    setMoreAnchorEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setMoreAnchorEl(null);
  };
  const navEnhancementsReady = useDeferredReady([Boolean(user)], 180);
  const roleAppearance = getRoleAppearance(user?.role, user?.approval?.accountType || user?.onboarding?.accountType);
  const pathname = location.pathname;
  const isPublicLanding = !user && pathname === '/';
  const showEnterpriseChip = Boolean(user) && !['admin', 'data-provider'].includes(user?.role);
  const navButtonSx = {
    whiteSpace: 'nowrap',
    minWidth: 0,
    px: { md: 0.75, xl: 1.25 },
    fontSize: { md: '0.95rem', xl: '1rem' },
  };
  const activeNavSx = {
    color: roleAppearance.accent,
    bgcolor: `${roleAppearance.accent}14`,
    boxShadow: `inset 0 -2px 0 ${roleAppearance.accent}`,
    '&:hover': {
      bgcolor: `${roleAppearance.accent}1f`,
    },
  };

  const isActiveRoute = (group) => {
    switch (group) {
      case 'search':
        return pathname.startsWith('/search');
      case 'benchmarks':
        return pathname.startsWith('/benchmarks') || pathname.startsWith('/models/');
      case 'marketplace':
        return pathname.startsWith('/marketplace') || pathname.startsWith('/datasets/') || pathname.startsWith('/compare');
      case 'annotator':
        return pathname.startsWith('/workbench') || pathname.startsWith('/annotator');
      case 'tasks':
        return pathname.startsWith('/tasks');
      case 'my-work':
        return pathname.startsWith('/my-work');
      case 'call-for-data':
        return pathname.startsWith('/call-for-data');
      case 'talent':
        return pathname.startsWith('/talent');
      case 'request':
        return pathname.startsWith('/request-dataset');
      case 'dashboard':
        return pathname.startsWith('/dashboard');
      case 'buyer-intel':
        return pathname.startsWith('/buyer-intel');
      case 'demand':
        return pathname.startsWith('/demand');
      case 'my-datasets':
        return pathname.startsWith('/my-datasets');
      case 'admin':
        return pathname.startsWith('/admin');
      case 'account':
        return pathname.startsWith('/settings');
      case 'login':
        return pathname.startsWith('/login');
      case 'register':
        return pathname.startsWith('/register');
      default:
        return false;
    }
  };

  const getNavItemSx = (group, extraSx = {}) => ({
    ...navButtonSx,
    ...(isActiveRoute(group) ? activeNavSx : {}),
    ...extraSx,
  });

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/');
  };

  const moreMenuItems = [
    {
      label: 'Buyer Intel',
      to: '/buyer-intel',
      show: canUseVendorTools(user) && canAccessModule(user, 'buyer-intel'),
      group: 'buyer-intel',
    },
    {
      label: 'Live Demand',
      to: '/demand',
      show: canUseVendorTools(user) && canAccessModule(user, 'demand'),
      group: 'demand',
    },
    {
      label: 'Workbench',
      to: '/workbench',
      show: canUseAnnotator(user) && canAccessModule(user, 'workbench'),
      group: 'annotator',
    },
    {
      label: 'My Work',
      to: '/my-work',
      show: canUseAnnotator(user) && canAccessModule(user, 'tasks'),
      group: 'my-work',
    },
    {
      label: 'My Datasets',
      to: '/my-datasets',
      show: canUseDatasetWorkspace(user) && canAccessModule(user, 'my-datasets'),
      group: 'my-datasets',
    },
    {
      label: 'Call for Data',
      to: '/call-for-data',
      show: canAccessModule(user, 'call-for-data'),
      group: 'call-for-data',
    },
    {
      label: 'Talent',
      to: '/talent',
      show: canInviteTalent(user) && canAccessModule(user, 'talent'),
      group: 'talent',
    },
    {
      label: 'Request Data',
      to: '/request-dataset',
      show: isBuyerOperator(user) && canAccessModule(user, 'request-data'),
      group: 'request',
    },
  ];

  const visibleMoreItems = moreMenuItems.filter((item) => item.show);
  const isMoreActive = visibleMoreItems.some((item) => isActiveRoute(item.group));

  return (
    <AppBar
      position="static"
      sx={{
        background: isPublicLanding ? '#100C1B' : roleAppearance.appBarBg,
        color: isPublicLanding ? '#FFFFFF' : 'inherit',
        borderBottomColor: isPublicLanding ? 'rgba(255,255,255,.1)' : 'divider',
        boxShadow: 'none',
      }}
    >
      <Toolbar
        sx={{
          minHeight: isPublicLanding ? '72px !important' : '64px !important',
          gap: 1.25,
          width: '100%',
          maxWidth: isPublicLanding ? 1536 : 'none',
          mx: 'auto',
          px: isPublicLanding ? { xs: 2.5, sm: 4, lg: 6 } : undefined,
        }}
      >
        <Box
          component={Link}
          to={user ? '/dashboard' : '/'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            textDecoration: 'none',
            color: 'inherit',
            flexShrink: 0,
          }}
        >
          <Avatar
            sx={{
              bgcolor: isPublicLanding ? '#6A4BD8' : roleAppearance.accent,
              width: isPublicLanding ? 30 : 32,
              height: isPublicLanding ? 30 : 32,
              borderRadius: isPublicLanding ? 0 : '50%',
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            DE
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>DataEdge</Typography>
          <Chip
            label={isPublicLanding ? 'Data intelligence' : (isActiveRoute('annotator') ? 'Workbench' : 'Marketplace')}
            size="small"
            variant="outlined"
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              ...(isPublicLanding ? { borderRadius: 0 } : {}),
              ml: 0.5,
              fontWeight: 700,
              ...(isPublicLanding ? { color: '#CFC3F8', borderColor: 'rgba(207,195,248,.28)' } : {}),
            }}
          />
          {showEnterpriseChip ? (
            <Chip
              label="Enterprise"
              size="small"
              sx={{
                display: { xs: 'none', xl: 'inline-flex' },
                bgcolor: 'primary.light',
                color: 'primary.contrastText',
                ml: 0.5,
              }}
            />
          ) : null}
          {roleAppearance.label ? (
            <Chip
              label={roleAppearance.label}
              size="small"
              sx={{
                display: { xs: 'none', lg: 'inline-flex' },
                bgcolor: roleAppearance.chipBg,
                color: roleAppearance.chipColor,
                ml: 0.25,
                fontWeight: 700,
              }}
            />
          ) : null}
        </Box>

        <Box
          sx={{
            display: { xs: 'none', xl: (user && canAccessModule(user, 'search')) ? 'flex' : 'none' },
            flex: 1,
            justifyContent: 'center',
            px: 1.5,
          }}
        >
          <Box
            sx={{
              width: { lg: 520, xl: 720 },
              maxWidth: '100%',
              transition: 'box-shadow 0.2s ease, background-color 0.2s ease',
              ...(isActiveRoute('search')
                ? {
                    boxShadow: `0 0 0 2px ${roleAppearance.accent}33`,
                    bgcolor: `${roleAppearance.accent}08`,
                  }
                : {}),
            }}
          >
            {navEnhancementsReady ? (
              <Suspense fallback={<Box sx={{ height: 44, bgcolor: 'rgba(255,255,255,0.14)' }} />}>
                <SmartSearchBar compact placeholder="Ask for datasets, benchmarks, or models" />
              </Suspense>
            ) : (
              <Box sx={{ height: 44, bgcolor: 'rgba(255,255,255,0.14)' }} />
            )}
          </Box>
        </Box>

        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5, minWidth: 0, ml: 'auto' }}>
          {user ? (
            <>
              <Button color="inherit" component={Link} to="/dashboard" sx={getNavItemSx('dashboard')}>
                Dashboard
              </Button>
              {canAccessModule(user, 'marketplace') ? (
                <Button color="inherit" component={Link} to="/marketplace" sx={getNavItemSx('marketplace')}>
                  Marketplace
                </Button>
              ) : null}
              {canAccessModule(user, 'benchmarks') ? (
                <Button color="inherit" component={Link} to="/benchmarks" sx={getNavItemSx('benchmarks')}>
                  Benchmarks
                </Button>
              ) : null}
              {canUseAnnotator(user) && canAccessModule(user, 'tasks') ? (
                <Button color="inherit" component={Link} to="/tasks" sx={getNavItemSx('tasks')}>
                  Tasks
                </Button>
              ) : null}
              {canAccessModule(user, 'search') ? (
                <Button
                  color="inherit"
                  component={Link}
                  to="/search"
                  startIcon={<AutoAwesome />}
                  sx={getNavItemSx('search', { display: { md: 'inline-flex', xl: 'none' } })}
                >
                  Smart Search
                </Button>
              ) : null}

              {visibleMoreItems.length > 0 ? (
                <>
                  <Button
                    color="inherit"
                    onClick={handleMoreMenu}
                    endIcon={<KeyboardArrowDownIcon />}
                    sx={getNavItemSx('more', isMoreActive ? activeNavSx : {})}
                  >
                    More
                  </Button>
                  <Menu
                    anchorEl={moreAnchorEl}
                    open={Boolean(moreAnchorEl)}
                    onClose={handleMoreClose}
                  >
                    {visibleMoreItems.map((item) => (
                      <MenuItem
                        key={item.to}
                        component={Link}
                        to={item.to}
                        onClick={handleMoreClose}
                        selected={isActiveRoute(item.group)}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              ) : null}

              {isAdmin(user) ? (
                <Button color="inherit" component={Link} to="/admin" sx={getNavItemSx('admin')}>
                  Admin Panel
                </Button>
              ) : null}
              {navEnhancementsReady ? (
                <Suspense fallback={<IconButton color="inherit" disabled aria-label="Notifications loading"><AccountCircle /></IconButton>}>
                  <NotificationCenter user={user} />
                </Suspense>
              ) : null}
              <IconButton
                color="inherit"
                onClick={handleMenu}
                aria-label="Account menu"
                sx={isActiveRoute('account') ? activeNavSx : undefined}
              >
                <AccountCircle />
              </IconButton>
            </>
          ) : (
            <>
              <Button
                color="inherit"
                onClick={(event) => setSolutionsAnchor(event.currentTarget)}
                endIcon={<ExpandMore sx={{ fontSize: 16 }} />}
                sx={{ ...navButtonSx, ...(isPublicLanding ? { color: '#C7BFCE' } : {}) }}
              >
                Solutions
              </Button>
              <Menu
                anchorEl={solutionsAnchor}
                open={Boolean(solutionsAnchor)}
                onClose={() => setSolutionsAnchor(null)}
                PaperProps={{ sx: { borderRadius: 0, minWidth: 210 } }}
              >
                {[
                  ['For AI teams', '/for/ai-teams'],
                  ['For data providers', '/for/data-providers'],
                  ['For experts', '/for/experts'],
                  ['For operators', '/for/operators'],
                ].map(([label, to]) => (
                  <MenuItem key={to} component={Link} to={to} onClick={() => setSolutionsAnchor(null)}>
                    {label}
                  </MenuItem>
                ))}
              </Menu>
              <Button color="inherit" component={Link} to="/login" sx={getNavItemSx('login', isPublicLanding ? { color: '#C7BFCE', px: 1.5 } : {})}>
                Sign in
              </Button>
              <Button
                variant={isPublicLanding ? 'text' : 'contained'}
                color="primary"
                component={Link}
                to="/register"
                sx={getNavItemSx('register', isPublicLanding
                  ? { bgcolor: '#FFFFFF', color: '#100C1B', borderRadius: 0, px: 2.2, '&:hover': { bgcolor: '#F0EBFF' } }
                  : (isActiveRoute('register') ? { color: 'primary.contrastText' } : {}))}
              >
                Register
              </Button>
            </>
          )}
        </Box>

        <Box sx={{ display: { xs: 'flex', md: 'none' }, ml: 'auto', gap: 1, alignItems: 'center' }}>
          {user ? (
            <IconButton color="inherit" onClick={handleMenu}>
              <MenuIcon />
            </IconButton>
          ) : (
            <>
              <Button color="inherit" component={Link} to="/login" sx={{ minWidth: 0, px: 1 }}>
                Sign in
              </Button>
              <Button variant="contained" component={Link} to="/register" sx={{ minWidth: 0, px: 1.5 }}>
                Register
              </Button>
            </>
          )}
        </Box>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          {user ? (
            <>
          <MenuItem selected={isActiveRoute('dashboard')} onClick={handleClose} component={Link} to="/dashboard">
            <Dashboard sx={{ mr: 2 }} />
            Dashboard
          </MenuItem>
          {canUseVendorTools(user) && canAccessModule(user, 'buyer-intel') ? (
            <MenuItem selected={isActiveRoute('buyer-intel')} onClick={handleClose} component={Link} to="/buyer-intel">
              <AutoAwesome sx={{ mr: 2 }} />
              Buyer Intel
            </MenuItem>
          ) : null}
          {canUseAnnotator(user) && canAccessModule(user, 'workbench') ? (
            <MenuItem selected={isActiveRoute('annotator')} onClick={handleClose} component={Link} to="/workbench">
              <Draw sx={{ mr: 2 }} /> DataEdge Workbench
            </MenuItem>
          ) : null}
          {canUseAnnotator(user) && canAccessModule(user, 'tasks') ? (
            <MenuItem selected={isActiveRoute('tasks')} onClick={handleClose} component={Link} to="/tasks">
              <AutoAwesome sx={{ mr: 2 }} /> Data collection tasks
            </MenuItem>
          ) : null}
          {canUseAnnotator(user) && canAccessModule(user, 'tasks') ? (
            <MenuItem selected={isActiveRoute('my-work')} onClick={handleClose} component={Link} to="/my-work">
              <Insights sx={{ mr: 2 }} />
              My Work
            </MenuItem>
          ) : null}
          {canAccessModule(user, 'marketplace') ? (
            <MenuItem selected={isActiveRoute('marketplace')} onClick={handleClose} component={Link} to="/marketplace">
              <Storefront sx={{ mr: 2 }} /> DataEdge Marketplace
            </MenuItem>
          ) : null}
          {canUseDatasetWorkspace(user) && canAccessModule(user, 'my-datasets') ? (
            <MenuItem selected={isActiveRoute('my-datasets')} onClick={handleClose} component={Link} to="/my-datasets">
              <FolderSpecial sx={{ mr: 2 }} />
              My Datasets
            </MenuItem>
          ) : null}
          <MenuItem selected={pathname.startsWith('/settings')} onClick={handleClose} component={Link} to="/settings">
            <SettingsIcon sx={{ mr: 2 }} />
            Settings
          </MenuItem>
          {canAccessModule(user, 'call-for-data') ? (
            <MenuItem selected={isActiveRoute('call-for-data')} onClick={handleClose} component={Link} to="/call-for-data">
              <FolderSpecial sx={{ mr: 2 }} />
              Call for Data
            </MenuItem>
          ) : null}
          {canInviteTalent(user) && canAccessModule(user, 'talent') ? (
            <MenuItem selected={isActiveRoute('talent')} onClick={handleClose} component={Link} to="/talent">
              <Group sx={{ mr: 2 }} />
              Talent
            </MenuItem>
          ) : null}
          {isBuyerOperator(user) && canAccessModule(user, 'request-data') ? <MenuItem selected={isActiveRoute('request')} onClick={handleClose} component={Link} to="/request-dataset">
            <Dashboard sx={{ mr: 2 }} /> Request Data
          </MenuItem> : null}
          {isAdmin(user) && (
            <MenuItem selected={pathname.startsWith('/admin')} onClick={handleClose} component={Link} to="/admin">
              <AdminPanelSettings sx={{ mr: 2 }} />
              Admin Panel
            </MenuItem>
          )}
          <MenuItem onClick={handleLogout}>
            <AccountCircle sx={{ mr: 2 }} />
            Logout
          </MenuItem>
            </>
          ) : null}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
